import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import { isMobile } from 'react-device-detect';
import DailyPerfCumRow from './DailyPerfCumRow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setPerfEndDate, setPerfStartDate } from '../../../actions';
import i18n from '../../../i18n';
import './DailyPerformance.css';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import isEqual from 'lodash/isEqual';
import { timelineColors } from './palette';

const customStyles = {
  control: (base) => ({ ...base, borderRadius: 0, boxShadow: 'none' }),
  option: (p) => ({
    ...p,
    color: '#5b6770',
    fontSize: 14,
    borderRadius: 0,
    backgroundColor: 'white',
    ':hover': { backgroundColor: '#050139', color: 'white' },
  }),
  menu: (b) => ({ ...b, borderRadius: 0 }),
  singleValue: (p, s) => ({
    ...p,
    opacity: s.isDisabled ? 0.5 : 1,
    transition: 'opacity 100ms',
  }),
  dropdownIndicator: (b) => ({ ...b, color: '#050139' }),
};

function formatMDYUTC(d) {
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}
function parseMDYUTC(str) {
  const [m, d, y] = str.split('/').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

class DailyPerformance extends Component {
  constructor(props) {
    super(props);
    const { data } = props;

    function getQtr(month) {
      if (month <= 3) return 1;
      if (month <= 6) return 4;
      if (month <= 9) return 7;
      return 10;
    }

    let startDate = null;
    let endDate = null;

    for (let i = 0; i < 6; i++) {
      if (data?.[i]?.levelsDaily?.date) {
        startDate = new Date(data[i].levelsDaily.date[0]);
        endDate = new Date(
          data[i].levelsDaily.date[data[i].levelsDaily.date.length - 1]
        );
        break;
      }
    }

    if (!startDate || !endDate) {
      const today = new Date();
      startDate = new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate()
      );
      endDate = today;
      console.warn(
        'DailyPerformance: startDate or endDate not found in data. Using default dates.'
      );
    }

    const startDateActual = new Date(startDate);
    startDateActual.setDate(startDateActual.getDate());

    const chartOptions = chartData(data, startDate, endDate, 'ror');
    this.state = {
      origDate: `${
        startDate.getMonth() + 1
      }/${startDate.getDate()}/${startDate.getFullYear()}`,
      startDate: `${
        startDateActual.getMonth() + 1
      }/${startDateActual.getDate()}/${startDateActual.getFullYear()}`,
      startDateOriginal: `${
        startDateActual.getMonth() + 1
      }/${startDateActual.getDate()}/${startDateActual.getFullYear()}`,
      startDateDisplay: `${
        startDateActual.getMonth() + 1
      }/${startDateActual.getDate()}/${startDateActual.getFullYear()}`,
      endDate: `${
        endDate.getMonth() + 1
      }/${endDate.getDate()}/${endDate.getFullYear()}`,
      endDateOriginal: `${
        endDate.getMonth() + 1
      }/${endDate.getDate()}/${endDate.getFullYear()}`,
      endDateDisplay: `${
        endDate.getMonth() + 1
      }/${endDate.getDate()}/${endDate.getFullYear()}`,
      mtdDate: `${endDate.getMonth() + 1}/1/${endDate.getFullYear()}`,
      qtdDate: `${getQtr(endDate.getMonth() + 1)}/1/${endDate.getFullYear()}`,
      ytdDate: `1/1/${endDate.getFullYear()}`,
      oneYDate: `${endDate.getMonth() + 1}/${endDate.getDate()}/${
        endDate.getFullYear() - 1
      }`,
      threeYDate: `${endDate.getMonth() + 1}/${endDate.getDate()}/${
        endDate.getFullYear() - 3
      }`,
      fiveYDate: `${endDate.getMonth() + 1}/${endDate.getDate()}/${
        endDate.getFullYear() - 5
      }`,
      tenYDate: `${endDate.getMonth() + 1}/${endDate.getDate()}/${
        endDate.getFullYear() - 10
      }`,
      chartType: { value: 'ror', label: 'Cumulative Total Return' },
      chartOptions,
      selectedRange: 'max',
    };

    this.echartsInstance = null;
  }

  componentDidMount() {
    const { perfStartDate, perfEndDate } = this.props;

    if (perfStartDate) {
      this.setState(
        {
          startDateDisplay: perfStartDate,
          selectedRange: determineRange(perfStartDate, this.state.origDate),
        },
        () => this.resetStartDate(perfStartDate)
      );
    } else {
      this.props.setPerfStartDate(this.state.startDate);
    }

    if (perfEndDate) {
      this.setState({ endDateDisplay: perfEndDate }, () =>
        this.resetEndDate(perfEndDate)
      );
    } else {
      this.props.setPerfEndDate(this.state.endDate);
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.data, this.props.data)) {
      const { chartType, startDate, endDate } = this.state;
      if (chartType?.value) {
        const chartOptions = chartData(
          this.props.data,
          startDate,
          endDate,
          chartType.value
        );
        this.setState({ chartOptions }, this.showTooltipOnLastDataPoint);
      }
    }
  }

  resetStartDate(dateStr) {
    const { chartType, endDate } = this.state;
    const { data } = this.props;
    if (!chartType?.value) return;
    const chartOptions = chartData(data, dateStr, endDate, chartType.value);
    this.setState(
      { startDate: dateStr, chartOptions },
      this.showTooltipOnLastDataPoint
    );
  }

  resetEndDate(dateStr) {
    const { chartType, startDate } = this.state;
    const { data } = this.props;
    if (!chartType?.value) return;
    const chartOptions = chartData(data, startDate, dateStr, chartType.value);
    this.setState(
      { endDate: dateStr, chartOptions },
      this.showTooltipOnLastDataPoint
    );
  }

  onStartInputChange = (e) =>
    this.setState({ startDateDisplay: e.target.value });
  onEndInputChange = (e) => this.setState({ endDateDisplay: e.target.value });

  onStartFormSubmit = (e) => {
    e.preventDefault();
    const dateStr = this.state.startDateDisplay;
    const d = new Date(dateStr);
    if (isNaN(d.getTime()))
      this.setState({ startDateDisplay: this.state.startDate });
    else {
      this.resetStartDate(dateStr);
      this.props.setPerfStartDate(dateStr);
    }
  };

  onEndFormSubmit = (e) => {
    e.preventDefault();
    const dateStr = this.state.endDateDisplay;
    const d = new Date(dateStr);
    if (isNaN(d.getTime()))
      this.setState({ endDateDisplay: this.state.endDate });
    else {
      this.resetEndDate(dateStr);
      this.props.setPerfEndDate(dateStr);
    }
  };

  onGoFormSubmit = () => {
    const { data } = this.props;
    const {
      chartType,
      startDateOriginal,
      endDateOriginal,
      startDateDisplay,
      endDateDisplay,
    } = this.state;

    const newStartDate = getNextBusinessDay(
      new Date(startDateDisplay),
      startDateOriginal
    );
    const newEndDate = getNextBusinessDay(
      new Date(endDateDisplay),
      endDateOriginal
    );

    const startDateChange = this.state.startDate !== startDateDisplay;
    const endDateChange = this.state.endDate !== endDateDisplay;

    const startDateFinal =
      newStartDate < new Date(startDateOriginal)
        ? new Date(startDateOriginal)
        : newStartDate;
    const endDateFinal =
      newEndDate > new Date(endDateOriginal)
        ? new Date(endDateOriginal)
        : newEndDate;

    const startDateDisplayFinal =
      newStartDate < new Date(startDateOriginal)
        ? startDateOriginal
        : startDateDisplay;
    const endDateDisplayFinal =
      newEndDate > new Date(endDateOriginal) ? endDateOriginal : endDateDisplay;

    const chartOptions = chartData(
      data,
      startDateFinal,
      endDateFinal,
      chartType.value
    );

    this.setState(
      {
        startDate: startDateFinal,
        endDate: endDateFinal,
        startDateDisplay: startDateDisplayFinal,
        endDateDisplay: endDateDisplayFinal,
        chartOptions,
      },
      () => {
        if (startDateChange) this.props.setPerfStartDate(startDateDisplayFinal);
        if (endDateChange) this.props.setPerfEndDate(endDateDisplayFinal);
        this.showTooltipOnLastDataPoint();
      }
    );
  };

  handleChartTypeChange = (chartType) => {
    const { startDate, endDate } = this.state;
    const { data } = this.props;
    if (!chartType?.value) return;
    const chartOptions = chartData(data, startDate, endDate, chartType.value);
    this.setState({ chartType, chartOptions }, this.showTooltipOnLastDataPoint);
  };

  setDate = async (theDate) => {
    let newDate = null;
    if (theDate === 'mtd') newDate = this.state.mtdDate;
    else if (theDate === 'qtd') newDate = this.state.qtdDate;
    else if (theDate === 'ytd') newDate = this.state.ytdDate;
    else if (theDate === '1y') newDate = this.state.oneYDate;
    else if (theDate === '3y') newDate = this.state.threeYDate;
    else if (theDate === '5y') newDate = this.state.fiveYDate;
    else if (theDate === '10y') newDate = this.state.tenYDate;
    else if (theDate === 'max') newDate = this.state.origDate;

    newDate = new Date(newDate);
    newDate.setDate(newDate.getDate());
    await this.setState({
      endDate: this.state.endDateOriginal,
      selectedRange: theDate,
    });
    const s = `${
      newDate.getMonth() + 1
    }/${newDate.getDate()}/${newDate.getFullYear()}`;
    await this.setState({ startDateDisplay: s });
    await this.resetStartDate(s);
    await this.props.setPerfStartDate(s);
  };

  handleDatazoomChange = (data) => {
    const { chartOptions } = this.state;
    const startIdx = Math.floor(
      chartOptions.xAxis.data.length * (data.start * 0.01)
    );
    const endIdx = Math.floor(
      chartOptions.xAxis.data.length * (data.end * 0.01)
    );
    if (chartOptions.xAxis.data[startIdx])
      this.setState({ startDateDisplay: chartOptions.xAxis.data[startIdx] });
    if (chartOptions.xAxis.data[endIdx])
      this.setState({ endDateDisplay: chartOptions.xAxis.data[endIdx] });
    this.showTooltipOnLastDataPoint();
  };

  onChartReady = (echartsInstance) => {
    this.echartsInstance = echartsInstance;
    this.showTooltipOnLastDataPoint();
  };

  showTooltipOnLastDataPoint = () => {
    const { chartOptions } = this.state;
    // const last =
    //   chartOptions?.xAxis?.data?.[chartOptions.xAxis.data.length - 1];
    //console.log('Last axis label:', last); // should be "8/15"
    if (!this.echartsInstance || !chartOptions?.series || !chartOptions?.xAxis)
      return;
    const lastDataIndex = chartOptions.xAxis.data.length - 1;
    chartOptions.series.forEach((_, seriesIndex) => {
      this.echartsInstance.dispatchAction({
        type: 'showTip',
        seriesIndex,
        dataIndex: lastDataIndex,
      });
    });
  };

  renderHeader() {
    const { startDateDisplay, endDateDisplay } = this.state;
    return (
      <thead>
        <tr>
          <th colSpan="5" className="textLeft">
            DAILY CUMULATIVE PERFORMANCE {i18n.t('from')} {startDateDisplay} to{' '}
            {endDateDisplay}
          </th>
        </tr>
        <tr>
          <th scope="col" className="textLeft">
            {i18n.t('Ticker')}
          </th>
          <th scope="col" className="textLeft">
            {i18n.t('Fund')}
          </th>
          <th scope="col" className="textCenter">
            {i18n.t('Inception')}
            <br /> {i18n.t('Date')}
          </th>
          <th className="borderleft borderbottom textCenter">
            {i18n.t('Cumulative Returns')}
          </th>
          <th className="borderleft borderbottom textCenter">
            {i18n.t('Average Annual Total Returns')}
          </th>
        </tr>
      </thead>
    );
  }

  renderRows() {
    const { startDateDisplay, endDateDisplay } = this.state;
    const chartDataFiltered = this.state.chartOptions.series;

    const startDateD = new Date(startDateDisplay);
    const endDateD = new Date(endDateDisplay);
    const diffDays =
      Math.ceil(Math.abs(endDateD - startDateD) / (1000 * 3600 * 24)) + 1;

    return chartDataFiltered.map((fund) => {
      if (!fund?.fundType) return null;
      // const getParsedFundName = (name) => {
      //   const parts = name.split(' - ');
      //   return parts.length > 1 ? parts[1] : name;
      // };
      return (
        <DailyPerfCumRow
          key={fund.name}
          fund={fund}
          diffDays={diffDays}
          fundType={fund.fundType}
          ms={fund.msUrl}
          url={fund.fundPageURL}
        />
      );
    });
  }

  render() {
    const {
      origDate,
      startDateDisplay,
      endDateDisplay,
      chartType,
      oneYDate,
      threeYDate,
      fiveYDate,
      tenYDate,
      chartOptions,
      selectedRange,
    } = this.state;

    return (
      <div className="fund-char-perf">
        <div className="chartHeader">{i18n.t('Daily NAV Performance')}</div>
        <div className="chartDynamicTitle">
          {`Performance from ${startDateDisplay} to ${endDateDisplay} - ${chartType.label}`}
        </div>
        <div className="row" style={{ marginBottom: '20px' }}>
          <div
            className="col-md-3"
            style={{ textAlign: 'center', paddingRight: '3px' }}
          >
            <Select
              options={[
                { value: 'ror', label: 'Cumulative Total Return' },
                { value: 'growth10k', label: 'Growth of 10K' },
              ]}
              styles={customStyles}
              value={chartType}
              onChange={(value) => this.handleChartTypeChange(value)}
            />
          </div>
          <div
            className="col-md-6"
            style={{
              textAlign: 'center',
              paddingLeft: '3px',
              paddingRight: '3px',
            }}
          >
            <button
              className={`dlyPerfbutton ${
                selectedRange === 'mtd' ? 'selected-other' : ''
              }`}
              onClick={() => this.setDate('mtd')}
            >
              MTD
            </button>
            <button
              className={`dlyPerfbutton ${
                selectedRange === 'qtd' ? 'selected-other' : ''
              }`}
              onClick={() => this.setDate('qtd')}
            >
              QTD
            </button>
            <button
              className={`dlyPerfbutton ${
                selectedRange === 'ytd' ? 'selected-other' : ''
              }`}
              onClick={() => this.setDate('ytd')}
            >
              YTD
            </button>
            {new Date(origDate) <= new Date(oneYDate) && (
              <button
                className={`dlyPerfbutton ${
                  selectedRange === '1y' ? 'selected-other' : ''
                }`}
                onClick={() => this.setDate('1y')}
              >
                1Y
              </button>
            )}
            {new Date(origDate) <= new Date(threeYDate) && (
              <button
                className={`dlyPerfbutton ${
                  selectedRange === '3y' ? 'selected-other' : ''
                }`}
                onClick={() => this.setDate('3y')}
              >
                3Y
              </button>
            )}
            {new Date(origDate) <= new Date(fiveYDate) && (
              <button
                className={`dlyPerfbutton ${
                  selectedRange === '5y' ? 'selected-other' : ''
                }`}
                onClick={() => this.setDate('5y')}
              >
                5Y
              </button>
            )}
            {new Date(origDate) <= new Date(tenYDate) && (
              <button
                className={`dlyPerfbutton ${
                  selectedRange === '10y' ? 'selected-other' : ''
                }`}
                onClick={() => this.setDate('10y')}
              >
                10Y
              </button>
            )}
            <button
              className={`dlyPerfbutton max-button ${
                selectedRange === 'max' ? 'selected-max' : ''
              }`}
              onClick={() => this.setDate('max')}
            >
              MAX
            </button>
          </div>
          <div
            className="col-md-3"
            style={{ textAlign: 'right', paddingLeft: '0px' }}
          >
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '90px',
                textAlign: 'right',
                paddingLeft: '3px',
              }}
            >
              <form onSubmit={this.onStartFormSubmit}>
                <input
                  name="startDate"
                  style={{
                    textAlign: 'center',
                    paddingLeft: '5px',
                    paddingRight: '5px',
                    height: '38px',
                    borderRadius: '0px',
                  }}
                  className="form-control"
                  onChange={this.onStartInputChange}
                  value={startDateDisplay}
                />
              </form>
            </div>
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '20px',
                textAlign: 'center',
                lineHeight: '38px',
                color: '#5b6770',
                fontSize: '14px',
              }}
            >
              to
            </div>
            <div
              style={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '90px',
                textAlign: 'right',
              }}
            >
              <form onSubmit={this.onEndFormSubmit}>
                <input
                  name="endDate"
                  style={{
                    textAlign: 'center',
                    paddingLeft: '5px',
                    paddingRight: '5px',
                    height: '38px',
                    borderRadius: '0px',
                  }}
                  className="form-control"
                  onChange={this.onEndInputChange}
                  value={endDateDisplay}
                />
              </form>
            </div>
            <button
              className="dlyPerfGoButton"
              onClick={() => this.onGoFormSubmit('go')}
            >
              {this.props.loadingPerformance ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                'GO'
              )}
            </button>
          </div>
        </div>

        <div className="chartContainer">
          <ReactEcharts
            option={chartOptions}
            ref={null}
            theme="default"
            notMerge={true}
            style={{ height: 500 }}
            onEvents={{ datazoom: this.handleDatazoomChange }}
            onChartReady={this.onChartReady}
          />
        </div>

        <div className="table-responsive">
          <table className="table tableFC">
            {this.renderHeader()}
            <tbody>{this.renderRows()}</tbody>
          </table>
        </div>
      </div>
    );
  }
}

function determineRange(perfStartDate, origDate) {
  return 'max';
}

function getNextBusinessDay(date, originalDate) {
  if (isNaN(Date.parse(date))) return originalDate;
  let nextDay = new Date(date);
  const day = nextDay.getDay();
  if (day === 6) nextDay.setDate(nextDay.getDate() + 2);
  else if (day === 0) nextDay.setDate(nextDay.getDate() + 1);
  return nextDay;
}

function getLegendData(data) {
  return data.map((fund) => `${fund.ticker} - ${fund.name}`);
}

function getXData(data) {
  let levelsDaily = null;
  for (let i = 0; i < 6; i++) {
    if (data?.[i]?.levelsDaily) {
      levelsDaily = data[i].levelsDaily;
      break;
    }
  }
  if (!levelsDaily) {
    console.warn('DailyPerformance: levelsDaily data not found.');
    return [];
  }

  return levelsDaily.date.map((value) => formatMDYUTC(new Date(value)));
}

// function cumSum(data) {
//   const cumulativeSum = (
//     (sum) => (value) =>
//       (sum += sum * value)
//   )(1);
//   const cums = data.map(cumulativeSum);
//   return cums.map((x) => (x - 1) * 100);
// }

// function growth10k(data) {
//   const growth = (
//     (sum) => (value) =>
//       (sum += sum * value)
//   )(10000);
//   return data.map(growth);
// }

function getLookupDate(seriesDate, fund) {
  let inputDate = new Date(seriesDate);
  inputDate.setUTCHours(16, 0, 0, 0);
  let isoDate = inputDate.toISOString();
  let lookupDate = isoDate.slice(0, isoDate.indexOf('.')) + 'Z';
  let idx = fund?.levelsDaily?.date?.indexOf(lookupDate);

  let counter = 0;
  while (idx === -1 && counter < 6) {
    inputDate.setDate(inputDate.getDate() + 1);
    isoDate = inputDate.toISOString();
    lookupDate = isoDate.slice(0, isoDate.indexOf('.')) + 'Z';
    idx = fund?.levelsDaily?.date?.indexOf(lookupDate);
    counter++;
  }

  counter = 0;
  while (idx === -1 && counter < 6) {
    inputDate.setDate(inputDate.getDate() - 2);
    isoDate = inputDate.toISOString();
    lookupDate = isoDate.slice(0, isoDate.indexOf('.')) + 'Z';
    idx = fund?.levelsDaily?.date?.indexOf(lookupDate);
    counter++;
  }
  return idx;
}

function cumprodFromReturns(returns) {
  let acc = 1;
  return returns.map((r) => (acc *= 1 + r));
}

function rebasedCumPct(levelsSlice) {
  const g = cumprodFromReturns(levelsSlice); // G_t
  const base = g[0]; // G_0 = 1 + r_0
  return g.map((v) => (v / base - 1) * 100); // (%)
}

function rebasedGrowth10k(levelsSlice) {
  const g = cumprodFromReturns(levelsSlice); // G_t
  const base = g[0];
  return g.map((v) => (v / base) * 10000); // (starts exactly at 10,000)
}

/**
 * Build series for the visible window AND normalize that window so it starts at 10,000.
 * We prepend a 0.0 daily return and duplicate the first date for alignment.
 */
function getSeriesData(data, startDate, endDate, chartType) {
  return data
    .map((fund) => {
      const series = {
        name: `${fund.ticker} - ${fund.name}`,
        fullName: fund.name,
        exchangeCode: fund.exchangeCode,
        fundType: fund.type,
        inceptionDate: fund.inceptionDate,
        fundPageURL: fund.fundPageURL,
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: { width: 2 },
      };

      const startIndex = getLookupDate(
        getNextBusinessDay(startDate, startDate),
        fund
      );
      const endIndex = getLookupDate(
        getNextBusinessDay(endDate, endDate),
        fund
      );

      const levelsSlice = fund?.levelsDaily?.dlyRoR?.slice(
        startIndex,
        endIndex + 1
      );
      const datesSlice = fund?.levelsDaily?.date?.slice(
        startIndex,
        endIndex + 1
      );

      if (
        !levelsSlice ||
        !datesSlice ||
        levelsSlice.length !== datesSlice.length
      )
        return null;

      if (chartType === 'growth10k') {
        series.data = rebasedGrowth10k(levelsSlice);
      } else if (chartType === 'ror') {
        series.data = rebasedCumPct(levelsSlice);
      }

      // Keep table & chart perfectly consistent
      series.nav10Knav = rebasedGrowth10k(levelsSlice); // now starts at 10,000
      series.nav10Kdates = datesSlice;
      series.cumSum = rebasedCumPct(levelsSlice); // now starts at 0.00%

      return series;
    })
    .filter(Boolean);
}

function dataFormatter(value, type, context = 'tooltip') {
  if (type === 'growth10k') {
    if (context === 'axis')
      return `$${Math.trunc(value).toLocaleString('en-US')}`;
    if (context === 'tooltip')
      return `$${value.toFixed(2).toLocaleString('en-US')}`;
  } else {
    if (context === 'axis') return `${value.toFixed(0)}%`;
    if (context === 'tooltip') return `${value.toFixed(2)}%`;
  }
  return value;
}

function chartData(data, startDate, endDate, chartType) {
  // Use the imported color palette
  const chartColors = timelineColors; // Adjust based on palette.js structure

  const legendData = getLegendData(data);
  let xaxisData = getXData(data);

  startDate = new Date(startDate);
  endDate = new Date(endDate);

  const startUTC = Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDate()
  );
  const endUTC = Date.UTC(
    endDate.getUTCFullYear(),
    endDate.getUTCMonth(),
    endDate.getUTCDate()
  );
  // Filter the formatted strings using UTC parsing
  xaxisData = xaxisData.filter((item) => {
    const t = parseMDYUTC(item).getTime();
    return t >= startUTC && t <= endUTC;
  });

  const seriesData = getSeriesData(data, startDate, endDate, chartType);

  // Ensure xAxis and series data lengths match exactly
  const seriesLength = seriesData?.[0]?.data?.length || 0;

  if (xaxisData.length > seriesLength) {
    xaxisData = xaxisData.slice(0, seriesLength);
  } else if (xaxisData.length < seriesLength) {
    const lastDate = new Date(xaxisData[xaxisData.length - 1]);
    while (xaxisData.length < seriesLength) {
      lastDate.setDate(lastDate.getDate() + 1);
      xaxisData.push(
        `${
          lastDate.getMonth() + 1
        }/${lastDate.getDate()}/${lastDate.getFullYear()}`
      );
    }
  }

  // Compute Y-axis min and max based on data
  let yMin = Infinity;
  let yMax = -Infinity;

  seriesData.forEach((series) => {
    series.data.forEach((value) => {
      if (value < yMin) yMin = value;
      if (value > yMax) yMax = value;
    });
  });

  // Handle flat or low-volatility scenarios
  const spread = yMax - yMin;
  const minYRange = chartType === 'growth10k' ? 500 : 5;

  if (spread < minYRange) {
    const center = (yMax + yMin) / 2;
    yMin = center - minYRange / 2;
    yMax = center + minYRange / 2;
  } else {
    const yAxisPadding = spread * 0.1; // 10% padding
    yMin = Math.floor((yMin - yAxisPadding) / 10) * 10;
    yMax = Math.ceil((yMax + yAxisPadding) / 10) * 10;
  }

  const widthPct = isMobile ? '15%' : '10%';

  return {
    title: {
      text: '',
      left: 5,
      textStyle: {
        fontStyle: 'normal',
        fontWeight: 900,
        fontSize: 14,
        color: '#8C959B',
      },
    },
    graphic: {
      type: 'image',
      id: 'logo',
      right: 10,
      top: 10,
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png', // Replace with your logo URL
        width: 100,
        height: 40,
      },
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        if (!params || params.length === 0) return '';

        let res = `<strong>${params[0].name}</strong>`; // Category name in bold

        params.forEach((param) => {
          if (param.value !== null && param.value !== undefined) {
            res += `<br/>${param.marker} <strong>${
              param.seriesName
            }</strong>: ${dataFormatter(param.value, chartType, 'tooltip')}`;
          }
        });

        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    legend: {
      icon: 'circle', // Ensures the legend icon is a circle
      itemGap: 5,
      data: legendData,
      width: '70%',
    },
    grid: {
      top: '10%',
      left: widthPct,
      right: widthPct,
      bottom: '15%',
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
      {
        type: 'slider',
        start: 0,
        end: 100,
        handleSize: '100%',
        handleStyle: {
          color: '#050139',
        },
        showDetail: false,
      },
    ],
    xAxis: {
      type: 'category',
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        rotate: 0,
      },
      data: xaxisData,
    },
    yAxis: {
      type: 'value',
      min: yMin,
      max: yMax,
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        formatter: function (value) {
          return dataFormatter(value, chartType, 'axis');
        },
      },
    },
    series: seriesData,
    color: chartColors, // Apply the palette colors here
  };
}

const mapStateToProps = (state) => ({
  data: state.fundData.data,
  perfStartDate: state.params.perfStartDate,
  wtRegion: state.params.wtRegion,
  loadingPerformance: state.fundData.loadingPerformance,
});

export default connect(mapStateToProps, { setPerfEndDate, setPerfStartDate })(
  DailyPerformance
);
